import { uuidToColor } from '$lib/helper/color';
import {
	type Booking,
	type BookingApi,
	BookingStatus,
	type BookingWithoutCustomer,
	type OrderOffer,
	ReconfirmationStatus,
	type SessionInstance
} from '$lib/model/booking';
import type { GuestPrice } from '$lib/model/offer';
import { BookingPaymentMethod, BookingPaymentStatus } from '$lib/model/payment';
import type { Icon } from '$lib/types/icons';
import type { Axios } from 'axios';
import { DateTime } from 'luxon';
import { derived, get, writable } from 'svelte/store';
import { apiStore } from './api';
import { calendarDateRange } from './calendarDates';
import { customerStore } from './customer';
import { offerStore } from './offer';
import { sessionInstanceStore } from './session-instance';
import { today } from './time';

export const bookingStore = writable<Booking[]>([]);
export const bookingDaysStore = writable<{ [key: string]: Booking[] }>({});
export const bookingHappeningDaysStore = derived([bookingDaysStore], ([$bookingDaysStore]) => {
	let out = {} as { [key: string]: Booking[] };
	Object.keys($bookingDaysStore).forEach((key) => {
		out[key] = $bookingDaysStore[key].filter((booking) => bookingIsHappening(booking));
	});
	return out;
});

bookingStore.subscribe((bookings) => {
	bookingDaysStore.set(
		bookings.reduce(
			(acc, booking) => {
				const date = booking.date.toISODate();
				if (!date) return acc;
				if (!acc[date]) acc[date] = [];
				acc[date].push(booking);
				return acc;
			},
			{} as { [key: string]: Booking[] }
		)
	);
});

export const lastBookingIds = writable<Booking['id'][]>([]);

export function convertApiResultToBooking(booking: BookingApi): Booking {
	booking.date = DateTime.fromISO(booking.date as unknown as string);
	booking.customer?.bookings.forEach((booking) => {
		booking.date = DateTime.fromISO(booking.date as unknown as string);
		booking.customFields = Array.isArray(booking.customFields) ? {} : booking.customFields;
	});

	booking.customFields = Array.isArray(booking.customFields) ? {} : booking.customFields;
	booking.payments.forEach((payment) => {
		payment.createdAt = DateTime.fromISO(payment.createdAt as unknown as string);
		payment.updatedAt = DateTime.fromISO(payment.updatedAt as unknown as string);
	});
	if (booking.surveyResult) {
		if (booking.surveyResult.answeredAt) {
			booking.surveyResult.answeredAt = DateTime.fromISO(
				booking.surveyResult.answeredAt as unknown as string
			);
		}
		if (booking.surveyResult.repliedAt) {
			booking.surveyResult.repliedAt = DateTime.fromISO(
				booking.surveyResult.repliedAt as unknown as string
			);
		}
	}
	addCustomerToStore(booking);
	return convertBookingApiToBooking(booking);
}

export function loadBooking(id: string, callback: (booking: Booking) => void = () => {}) {
	(get(apiStore) as Axios)
		.post<BookingApi[]>(`/booking/search`, {
			id: id
		})
		.then((res) => {
			addBookingToStore(res.data);
			res.data.forEach((booking: BookingApi) => callback(convertApiResultToBooking(booking)));
		});
}

function addBookingToStore(bookings: BookingApi[]) {
	bookingStore.set([
		...get(bookingStore).filter((b) => !bookings.some((newBooking) => newBooking.id === b.id)),
		...bookings.map((booking) => convertApiResultToBooking(booking))
	]);
}

export function clearBookingStore() {
	bookingStore.set([]);
}

export function loadBookings(date: DateTime | undefined = undefined, offset = 0) {
	const offsetLoading = 250;
	(get(apiStore) as Axios)
		.post('/booking/search?offset=' + offset + '&limit=' + offsetLoading, {
			startDate: date ? date.toISODate() : get(calendarDateRange).start.toISODate(),
			endDate: date ? date.toISODate() : get(calendarDateRange).end.toISODate()
		})
		.then((res) => {
			addBookingToStore(res.data);
			if (res?.data?.length === offsetLoading) {
				loadBookings(date, offset + offsetLoading);
			}
		});
}

export function convertBookingToBookingApi(booking: Booking): BookingApi {
	const customer = get(customerStore).find((c) => c.id === booking.customerId);
	const out = {
		...JSON.parse(JSON.stringify(booking)),
		payments: booking.payments,
		customer: customer
			? {
					...customer
				}
			: undefined,
		sessionInstances: []
	};

	out.date = DateTime.fromISO(out.date);

	return out;
}

export function updateBookingStore(booking: BookingApi) {
	bookingStore.set([
		...get(bookingStore).filter((b) => b.id !== booking.id),
		convertBookingApiToBooking(booking)
	]);
}

export function addCustomerToStore(booking: BookingApi) {
	if (booking.customer) {
		customerStore.set([
			...get(customerStore).filter((c) => c.id !== booking.customer?.id),
			{
				...booking.customer,
				bookings: booking.customer.bookings
			}
		]);
	}
}

export function convertBookingApiToBooking(booking: BookingApi): Booking {
	// Convert to Booking type and delete booking.customer
	const out: BookingApi = {
		...booking,
		date: DateTime.fromISO(booking.date as unknown as string)
	};
	const customerId = out.customer?.id;
	delete out.customer;
	return {
		...out,
		customerId: customerId
	};
	// End of conversion
}

export function getBookingPax(booking: BookingWithoutCustomer): number | undefined {
	const res = Math.max(
		getOfferPax(booking) ?? 0,
		...booking.sessions.map((session) => session.pax)
	);
	return res === -Infinity ? undefined : res;
}

export function getOfferPax(booking: BookingWithoutCustomer): number | undefined {
	const offers = booking.order.offers;
	const res = Math.max(
		...offers.map((offer) => offer.prices.reduce((acc, price) => acc + price.quantity, 0))
	);
	return res === -Infinity ? undefined : res;
}

type Setting = {
	status: BookingStatus;
	icon: Icon;
	bgColor: string;
	beforeDate: boolean | undefined;
	hidden: boolean;
	allowSummaryEmail?: boolean;
	containerClasses: string;
	iconClasses: string;
};

export const bookingStatusSettings: Setting[] = [
	{
		status: BookingStatus.Intent,
		icon: 'internetEarth',
		bgColor: 'disabled',
		beforeDate: undefined,
		hidden: true,
		allowSummaryEmail: false,
		containerClasses:
			'bg-disabled dark:bg-disabled-dark text-disabled-on dark:text-disabled-on-dark',
		iconClasses: 'w-5 h-5'
	},
	{
		status: BookingStatus.ToBePaid,
		icon: 'creditCard',
		bgColor: 'disabled',
		beforeDate: undefined,
		hidden: true,
		allowSummaryEmail: false,
		containerClasses:
			'bg-disabled dark:bg-disabled-dark text-disabled-on dark:text-disabled-on-dark',
		iconClasses: 'w-5 h-5 p-0.5'
	},
	{
		status: BookingStatus.Booked,
		icon: 'loading',
		bgColor: 'disabled',
		beforeDate: undefined,
		hidden: true,
		allowSummaryEmail: false,
		containerClasses:
			'bg-disabled dark:bg-disabled-dark text-disabled-on dark:text-disabled-on-dark',
		iconClasses: 'w-5 h-5 p-0.5'
	},
	{
		status: BookingStatus.ToBeConfirmed,
		icon: 'magnify',
		bgColor: 'warning',
		beforeDate: false,
		hidden: true,
		allowSummaryEmail: true,
		containerClasses:
			'bg-warning dark:bg-warning-dark text-warning-on dark:text-warning-on-dark',
		iconClasses: 'w-5 h-5 p-0.5'
	},
	{
		status: BookingStatus.ToBeReconfirmed,
		icon: 'email',
		bgColor: 'warning',
		beforeDate: false,
		hidden: false,
		allowSummaryEmail: true,
		containerClasses:
			'bg-warning dark:bg-warning-dark text-warning-on dark:text-warning-on-dark',
		iconClasses: 'w-5 h-5 p-0.5'
	},
	{
		status: BookingStatus.Confirmed,
		icon: 'check',
		bgColor: 'success',
		beforeDate: undefined,
		hidden: false,
		allowSummaryEmail: true,
		containerClasses: 'text-success dark:text-success-dark',
		iconClasses: 'w-5 h-5 '
	},
	{
		status: BookingStatus.Left,
		icon: 'broom',
		bgColor: 'secondary',
		beforeDate: true,
		hidden: false,
		allowSummaryEmail: false,
		containerClasses: 'bg-end dark:bg-end-dark text-end-on dark:text-end-on-dark',
		iconClasses: 'w-5 h-5 p-0.5'
	},
	{
		status: BookingStatus.Canceled,
		icon: 'trash',
		bgColor: 'end',
		beforeDate: undefined,
		hidden: false,
		allowSummaryEmail: false,
		containerClasses: 'bg-end dark:bg-end-dark text-end-on dark:text-end-on-dark',
		iconClasses: 'w-5 h-5 p-1'
	},
	{
		status: BookingStatus.NoShow,
		icon: 'ghost',
		bgColor: 'error',
		beforeDate: true,
		hidden: false,
		allowSummaryEmail: false,
		containerClasses: 'bg-error dark:bg-error-dark text-error-on dark:text-error-on-dark',
		iconClasses: 'w-5 h-5 p-1'
	},
	{
		status: BookingStatus.Expired,
		icon: 'wind',
		bgColor: 'end',
		beforeDate: undefined,
		hidden: true,
		allowSummaryEmail: false,
		containerClasses: 'bg-end dark:bg-end-dark text-end-on dark:text-end-on-dark',
		iconClasses: 'w-5 h-5 p-0.5'
	}
];

export function getColorStatus(status: BookingStatus) {
	return (
		bookingStatusSettings.find((setting) => setting.status === status)?.bgColor ?? 'secondary'
	);
}

export function getBookingSessionInstances(booking: BookingWithoutCustomer): SessionInstance[] {
	return booking.sessions
		.map((session) => session.sessionInstanceId)
		.map((sessionInstanceId) =>
			get(sessionInstanceStore).find(
				(sessionInstance) => sessionInstance.sessionInstanceId === sessionInstanceId
			)
		)
		.filter((sessionInstance) => sessionInstance !== undefined)
		.map((sessionInstance) => sessionInstance as SessionInstance);
}

export function bookingIsPaid(booking: Booking): boolean {
	const total = bookingGetTotalPrice(booking);

	return (
		Math.round(
			(total -
				booking.payments
					.filter(
						(payment) =>
							payment.status === BookingPaymentStatus.SUCCESS ||
							payment.status === BookingPaymentStatus.REFUNDED
					)
					.reduce((acc, payment) => acc + payment.amount - payment.amountRefunded, 0)) *
				100
		) /
			100 ===
		0
	);
}

export function bookingGetTotalPrice(booking: BookingWithoutCustomer): number {
	return (
		booking.order.offers.reduce(
			(acc, offerOrder) =>
				acc +
				// Offer base price
				(get(offerStore).find((offer) => offer.id === offerOrder.offerId)?.basePrice ?? 0) +
				// Guest prices
				offerOrder.prices.reduce((acc, price) => acc + price.quantity * price.unitPrice, 0),
			0
		) ?? 0
	);
}

export function bookingGetRemainingAmount(booking: BookingWithoutCustomer): number {
	const total = bookingGetTotalPrice(booking);
	const remaining =
		Math.round(
			(total -
				booking.payments
					.filter(
						(payment) =>
							payment.status === BookingPaymentStatus.SUCCESS ||
							payment.status === BookingPaymentStatus.REFUNDED
					)
					.reduce((acc, payment) => acc + payment.amount - payment.amountRefunded, 0)) *
				100
		) / 100;

	return remaining;
}

export function bookingHasSurveyResult(booking: Booking): boolean {
	return !!booking.surveyResult?.rating;
}

export function bookingIsHappening(booking: BookingWithoutCustomer): boolean {
	return (
		booking.status !== BookingStatus.Canceled &&
		booking.status !== BookingStatus.NoShow &&
		booking.status !== BookingStatus.Expired
	);
}

export function bookingIsEnded(booking: BookingWithoutCustomer): boolean {
	return (
		booking.status === BookingStatus.Canceled ||
		booking.status === BookingStatus.NoShow ||
		booking.status === BookingStatus.Expired ||
		booking.status === BookingStatus.Left
	);
}

export function bookingIsValid(booking: BookingWithoutCustomer): boolean {
	return (
		booking.status !== BookingStatus.Expired &&
		booking.status !== BookingStatus.Intent &&
		booking.status !== BookingStatus.ToBePaid
	);
}

export function bookingIsConfirmed(booking: BookingWithoutCustomer): boolean {
	return (
		booking.status !== BookingStatus.Canceled &&
		booking.status !== BookingStatus.NoShow &&
		booking.status !== BookingStatus.Expired &&
		booking.status !== BookingStatus.Intent &&
		booking.status !== BookingStatus.ToBePaid
	);
}

export function bookingIncoming(booking: BookingWithoutCustomer): boolean {
	return (
		booking.status !== BookingStatus.Canceled &&
		booking.status !== BookingStatus.NoShow &&
		booking.status !== BookingStatus.Expired &&
		booking.status !== BookingStatus.Left &&
		booking.date >= get(today)
	);
}

export const getBookingColor = derived(
	[uuidToColor],
	([$uuidToColor]) =>
		(booking: Booking | undefined) => {
			if (!booking) return '';
			return $uuidToColor(booking.id);
		}
);

export function bookingHasOnlinePaymentToBeRefunded(booking: Booking): boolean {
	return booking.payments.some(
		(p) =>
			(p.method === BookingPaymentMethod.ONLINE ||
				p.method === BookingPaymentMethod.GIFTCARD) &&
			(p.status === BookingPaymentStatus.SUCCESS ||
				p.status === BookingPaymentStatus.REFUNDED) &&
			p.amountRefunded < p.amount
	);
}

export function bookingStatusUpdate(booking: Booking, status: BookingStatus): void {
	booking.status = status;
	get(apiStore)?.put(`booking/${booking.id}/status`, { status: booking.status });
	bookingStore.set(get(bookingStore).map((b) => (b.id === booking.id ? booking : b)));
}

export function bookingConfirmedByCustomer(booking: BookingWithoutCustomer): void {
	booking.status = BookingStatus.Confirmed;
	booking.reconfirmationStatus = ReconfirmationStatus.Reconfirmed;
}

export function sendBookingSummaryEmail(
	booking: BookingWithoutCustomer
): Promise<void> | undefined {
	return get(apiStore)?.post(`booking/${booking.id}/send-summary`);
}

export function bookingMarkAsRead(booking: BookingWithoutCustomer): void {
	if (!booking.unread) return;
	booking.unread = false;
	get(apiStore)?.put(`booking/${booking.id}/read`);
	bookingStore.set(
		get(bookingStore).map((b) => (b.id === booking.id ? { ...b, unread: false } : b))
	);
}

export function bookingMarkAsUnread(booking: BookingWithoutCustomer): void {
	booking.unread = true;
	get(apiStore)?.put(`booking/${booking.id}/unread`);
	bookingStore.set(
		get(bookingStore).map((b) => (b.id === booking.id ? { ...b, unread: true } : b))
	);
}

export function getBookingGuestPricePax(orderOffer: OrderOffer, guestPrice: GuestPrice): number {
	return orderOffer.prices.find((p) => p.priceId === guestPrice.id)?.quantity ?? 0;
}
